<div class="legal-page">
  <h1>Privacy Policy</h1>
  <p class="last-updated">Last Updated: November 11, 2025</p>

  <section>
    <h2>Our Commitment to Your Privacy</h2>
    <p>
      This website operates on a <strong>privacy-first architecture</strong>. All tools run entirely in your browser.
      Your files, data, and preferences never leave your device unless you explicitly choose to send them elsewhere.
    </p>
  </section>

  <section>
    <h2>What Data We Collect</h2>

    <h3>1. Local Storage Data</h3>
    <p>
      Some applications store preferences and data in your browser's localStorage to provide a better experience:
    </p>
    <ul>
      <li><strong>Weather Notifications:</strong> Email address, location coordinates, weather preferences</li>
      <li><strong>Calculator:</strong> Calculation history (tape)</li>
      <li><strong>Rummy Score Keeper:</strong> Game scores and player names</li>
      <li><strong>Travel Checklist:</strong> Checklist items and templates</li>
      <li><strong>Fill & Sign:</strong> Signature library (your digital signatures)</li>
      <li><strong>GitHub Markdown Viewer:</strong> GitHub Personal Access Token (if provided), bookmarks, cached files</li>
      <li><strong>Home Page:</strong> Pinned apps and customization preferences</li>
    </ul>
    <p>
      <strong>Important:</strong> This data is stored locally on your device. We do not have access to it.
      It is never transmitted to our servers (we don't have servers). If you clear your browser data, this information will be deleted.
    </p>

    <h3>2. Email Addresses (Weather Notifications Only)</h3>
    <p>
      If you enable email notifications for weather alerts:
    </p>
    <ul>
      <li>Your email address and weather preferences are sent to our Cloudflare Worker</li>
      <li>This data is stored in Cloudflare KV (a key-value database)</li>
      <li>Used solely to send you weather notifications matching your preferences</li>
      <li>You can unsubscribe at any time using the link in notification emails</li>
    </ul>

    <h3>3. API Requests</h3>
    <p>
      Some applications make requests to third-party services:
    </p>
    <ul>
      <li><strong>Weather Notifications:</strong> Sends your location coordinates to <a href="https://open-meteo.com" target="_blank" rel="noopener">Open-Meteo API</a> to fetch weather forecasts</li>
      <li><strong>GitHub Markdown Viewer:</strong> Sends repository/file requests to <a href="https://api.github.com" target="_blank" rel="noopener">GitHub API</a> (if you provide a Personal Access Token, it's sent with requests)</li>
    </ul>
    <p>
      These third-party services have their own privacy policies. We recommend reviewing them:
    </p>
    <ul>
      <li>Open-Meteo: <a href="https://open-meteo.com/en/terms" target="_blank" rel="noopener">https://open-meteo.com/en/terms</a></li>
      <li>GitHub: <a href="https://docs.github.com/en/site-policy/privacy-policies/github-privacy-statement" target="_blank" rel="noopener">GitHub Privacy Statement</a></li>
    </ul>

    <h3>4. Analytics (Optional)</h3>
    <p>
      If configured, we may collect anonymized usage statistics via Cloudflare Workers Analytics:
    </p>
    <ul>
      <li>Which apps are used (no personal information)</li>
      <li>Page views and app launches</li>
      <li>No IP addresses or identifiable information</li>
    </ul>
    <p>
      This is <strong>privacy-first analytics</strong>—no cookies, no tracking pixels, no third-party analytics services.
    </p>

    <h3>5. PDF Password Handling</h3>
    <p>
      For PDF password tools, passwords are used only in-memory to decrypt documents on your device.
      Passwords are not stored in localStorage, IndexedDB, logs, or transmitted to our servers.
    </p>
  </section>

  <section>
    <h2>What Data We Do NOT Collect</h2>
    <ul>
      <li>❌ <strong>Files you upload:</strong> PDFs, images, audio, video—all processed locally in your browser</li>
      <li>❌ <strong>Tracking cookies:</strong> We don't use Google Analytics, Facebook Pixel, or any tracking scripts</li>
      <li>❌ <strong>IP addresses:</strong> We don't log your IP address</li>
      <li>❌ <strong>Browsing history:</strong> We don't track which pages you visit</li>
      <li>❌ <strong>Device information:</strong> We don't collect device fingerprints</li>
    </ul>
  </section>

  <section>
    <h2>How Your Data is Used</h2>
    <ul>
      <li><strong>Functionality:</strong> To provide the features you requested (saving preferences, sending notifications)</li>
      <li><strong>No advertising:</strong> We never sell or share your data with advertisers</li>
      <li><strong>No profiling:</strong> We don't build user profiles or track behavior</li>
    </ul>
  </section>

  <section>
    <h2>Data Storage & Security</h2>

    <h3>Local Storage</h3>
    <p>
      Data stored in localStorage remains on your device. Security considerations:
    </p>
    <ul>
      <li><strong>Shared devices:</strong> If you share your device, others may access locally stored data</li>
      <li><strong>Browser extensions:</strong> Malicious browser extensions can read localStorage</li>
      <li><strong>Recommendation:</strong> Use private/incognito mode on public computers, or use the "Clear All Data" button (see below)</li>
    </ul>

    <h3>Cloudflare Infrastructure</h3>
    <p>
      This website is hosted on Cloudflare Pages. Weather notification data is stored in Cloudflare KV.
      Cloudflare's security practices: <a href="https://www.cloudflare.com/trust-hub/" target="_blank" rel="noopener">Cloudflare Trust Hub</a>
    </p>
  </section>

  <section>
    <h2>Your Rights & Data Control</h2>

    <h3>Right to Access</h3>
    <p>
      All localStorage data is accessible in your browser's Developer Tools → Application → Local Storage.
    </p>

    <h3>Right to Deletion</h3>
    <p>
      You can delete your data at any time:
    </p>
    <ul>
      <li><strong>Local data:</strong> Use the "Clear All Data" button (found in Settings or app-specific controls)</li>
      <li><strong>Weather notifications:</strong> Click "Unsubscribe" in any notification email</li>
      <li><strong>Browser data:</strong> Clear your browser's site data (Settings → Privacy → Clear browsing data)</li>
    </ul>

    <h3>Right to Portability</h3>
    <p>
      localStorage data is stored in standard JSON format and can be exported using browser developer tools.
    </p>
  </section>

  <section>
    <h2>GDPR Compliance (EU Users)</h2>
    <p>
      If you are located in the European Union:
    </p>
    <ul>
      <li><strong>Legal basis:</strong> We process data based on your consent (opt-in for email notifications) and legitimate interest (providing requested functionality)</li>
      <li><strong>Data controller:</strong> See "Contact" section below</li>
      <li><strong>Data transfers:</strong> Data may be processed in the US (Cloudflare infrastructure), protected by standard contractual clauses</li>
      <li><strong>Your rights:</strong> Access, rectification, erasure, restriction, portability, objection—contact us to exercise these rights</li>
    </ul>
  </section>

  <section>
    <h2>CCPA Compliance (California Users)</h2>
    <p>
      If you are a California resident:
    </p>
    <ul>
      <li><strong>Categories collected:</strong> Email addresses (if opted in), location coordinates (for weather), usage statistics (anonymized)</li>
      <li><strong>Purpose:</strong> Provide requested services (weather notifications, app functionality)</li>
      <li><strong>No sale:</strong> We do not sell your personal information</li>
      <li><strong>Your rights:</strong> Right to know, right to delete, right to opt-out of sale (N/A)—contact us to exercise these rights</li>
    </ul>
  </section>

  <section>
    <h2>Children's Privacy (COPPA)</h2>
    <p>
      This website is not intended for children under 13. We do not knowingly collect personal information from children.
      If you are a parent/guardian and believe your child has provided us with information, please contact us to delete it.
    </p>
  </section>

  <section>
    <h2>Third-Party Links</h2>
    <p>
      Our website may contain links to external sites (e.g., GitHub, Open-Meteo). We are not responsible for their privacy practices.
      Please review their privacy policies before providing any information.
    </p>
  </section>

  <section>
    <h2>Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date.
      Continued use of the website after changes constitutes acceptance of the updated policy.
    </p>
  </section>

  <section>
    <h2>Contact</h2>
    <p>
      If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us:
    </p>
    <ul>
      <li><a href="#/feedback">Send feedback through the website</a></li>
      <li>Email <a href="mailto:contact@spastudio.org">contact@spastudio.org</a></li>
    </ul>
  </section>
</div>

<style>
  .legal-page {
    max-width: 760px;
    margin: 0 auto;
    padding: 2rem 1.5rem 4rem;
    color: hsl(var(--theme-foreground));
  }
  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 0.375rem;
    letter-spacing: -0.02em;
    color: hsl(var(--theme-foreground));
  }
  .last-updated {
    color: hsl(var(--theme-muted-foreground));
    font-size: 0.8125rem;
    margin-bottom: 2.5rem;
  }
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 2.5rem;
    margin-bottom: 0.75rem;
    color: hsl(var(--theme-foreground));
    border-bottom: 1px solid hsl(var(--theme-border) / 0.3);
    padding-bottom: 0.5rem;
    letter-spacing: -0.01em;
  }
  h3 {
    font-size: 1rem;
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    color: hsl(var(--theme-foreground));
  }
  section { margin-bottom: 1.5rem; }
  p {
    font-size: 0.9375rem;
    line-height: 1.7;
    margin-bottom: 0.875rem;
    color: hsl(var(--theme-muted-foreground));
  }
  ul {
    margin: 0.75rem 0;
    padding-left: 1.25rem;
    line-height: 1.7;
  }
  li {
    margin-bottom: 0.375rem;
    font-size: 0.9375rem;
    color: hsl(var(--theme-muted-foreground));
  }
  strong { font-weight: 600; color: hsl(var(--theme-foreground)); }
  a {
    color: hsl(var(--theme-primary));
    text-decoration: none;
    border-bottom: 1px solid hsl(var(--theme-primary) / 0.3);
    transition: border-color 200ms ease;
  }
  a:hover { border-color: hsl(var(--theme-primary)); }
  @media (max-width: 640px) {
    .legal-page { padding: 1.5rem 1rem 3rem; }
    h1 { font-size: 1.5rem; }
    h2 { font-size: 1.125rem; }
  }
</style>
